import React, { useEffect, useState } from 'react';
import { brandDailyConfigApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Save, Plus, X, Calendar as CalendarIcon, Sparkles } from 'lucide-react';

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/** Editor for one brand row. Local state, parent owns persistence trigger. */
function BrandRow({ cfg, onChange, onSave, saving }) {
  const setWeight = (idx, value) => {
    const next = { ...cfg.weights, [String(idx)]: value };
    onChange({ ...cfg, weights: next });
  };
  const toggleClosedWeekday = (idx) => {
    const set = new Set(cfg.closed_weekdays || []);
    if (set.has(idx)) set.delete(idx); else set.add(idx);
    onChange({ ...cfg, closed_weekdays: Array.from(set).sort() });
  };
  const addHoliday = () => {
    const today = new Date().toISOString().slice(0, 10);
    if ((cfg.holidays || []).includes(today)) return;
    onChange({ ...cfg, holidays: [...(cfg.holidays || []), today].sort() });
  };
  const updateHoliday = (i, val) => {
    const next = [...(cfg.holidays || [])];
    next[i] = val;
    onChange({ ...cfg, holidays: next });
  };
  const removeHoliday = (i) => {
    const next = (cfg.holidays || []).filter((_, j) => j !== i);
    onChange({ ...cfg, holidays: next });
  };

  return (
    <div
      className="border border-slate-200 bg-white p-5 space-y-5"
      data-testid={`brand-daily-row-${cfg.brand_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-lg font-semibold">{cfg.brand_name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            I pesi sono relativi: la somma dei target giornalieri = budget mensile.
            Lasciali tutti a 1.0 per la distribuzione piatta tradizionale.
          </p>
        </div>
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-black text-white hover:bg-slate-900 rounded-none"
          data-testid={`brand-daily-save-${cfg.brand_id}`}
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvataggio…' : 'Salva'}
        </Button>
      </div>

      {/* Weights row */}
      <div>
        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
          Pesi giornalieri
        </p>
        <div className="grid grid-cols-7 gap-2">
          {WEEKDAY_LABELS.map((label, idx) => (
            <div key={label} className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 text-center">
                {label}
              </span>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={cfg.weights?.[String(idx)] ?? 1}
                onChange={(e) => setWeight(idx, e.target.value === '' ? '' : Number(e.target.value))}
                className="rounded-none text-center tabular-nums"
                data-testid={`brand-daily-weight-${cfg.brand_id}-${idx}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Closed weekdays toggles */}
      <div>
        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
          Giorni di chiusura ricorrenti
        </p>
        <div className="flex flex-wrap gap-2">
          {WEEKDAY_LABELS.map((label, idx) => {
            const active = (cfg.closed_weekdays || []).includes(idx);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleClosedWeekday(idx)}
                className={`px-3 py-1.5 text-xs border transition-colors ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
                }`}
                data-testid={`brand-daily-closed-${cfg.brand_id}-${idx}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Holidays */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            Festivi specifici (chiuso)
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={addHoliday}
            className="rounded-none h-7 px-2 text-xs"
            data-testid={`brand-daily-add-holiday-${cfg.brand_id}`}
          >
            <Plus className="w-3 h-3 mr-1" />
            Aggiungi data
          </Button>
        </div>
        {(cfg.holidays || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">Nessun festivo configurato.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {(cfg.holidays || []).map((d, i) => (
              <div key={`holiday-${d}-${i}`} className="flex items-center gap-1">
                <Input
                  type="date"
                  value={d}
                  onChange={(e) => updateHoliday(i, e.target.value)}
                  className="rounded-none"
                  data-testid={`brand-daily-holiday-${cfg.brand_id}-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeHoliday(i)}
                  className="p-1.5 hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
                  aria-label="Rimuovi data"
                  data-testid={`brand-daily-holiday-remove-${cfg.brand_id}-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrandDailyConfigPanel() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setLoading(true);
    brandDailyConfigApi.list()
      .then((res) => setConfigs(res.data || []))
      .catch(() => setConfigs([]))
      .finally(() => setLoading(false));
  }, []);

  const updateCfg = (idx, next) => {
    setConfigs((prev) => prev.map((c, i) => (i === idx ? next : c)));
  };

  const saveCfg = async (cfg) => {
    setSavingId(cfg.brand_id);
    setFeedback(null);
    try {
      // Coerce empty strings → 0 before sending; the backend rejects non-numeric.
      const cleanWeights = {};
      for (const [k, v] of Object.entries(cfg.weights || {})) {
        cleanWeights[k] = v === '' || v == null ? 0 : Number(v);
      }
      await brandDailyConfigApi.upsert(cfg.brand_id, {
        weights: cleanWeights,
        closed_weekdays: cfg.closed_weekdays || [],
        holidays: cfg.holidays || [],
      });
      setFeedback({ type: 'ok', message: `Pesi salvati per ${cfg.brand_name}.` });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Errore salvataggio.';
      setFeedback({ type: 'err', message: detail });
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-slate-500 p-6" data-testid="brand-daily-loading">
        Caricamento configurazioni…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="brand-daily-panel">
      <div className="flex items-start gap-3 border border-slate-200 bg-slate-50 p-4">
        <Sparkles className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 space-y-1">
          <p className="font-semibold">Distribuzione giornaliera per brand</p>
          <p className="text-xs text-slate-600">
            Configura quanto ogni giorno della settimana pesa rispetto alla media — es. il sabato vale 1.6x di un lunedì —
            e indica i giorni di chiusura ricorrenti o le date specifiche di festivo. Il calendario userà questi
            valori per calcolare i target giornalieri e colorare il semaforo correttamente, anche per i giorni di chiusura.
          </p>
        </div>
      </div>

      {feedback && (
        <div
          className={`text-sm p-3 border ${
            feedback.type === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : 'border-red-300 bg-red-50 text-red-800'
          }`}
          data-testid="brand-daily-feedback"
        >
          {feedback.message}
        </div>
      )}

      {configs.length === 0 ? (
        <div className="text-sm text-slate-500 p-6 border border-dashed border-slate-300 text-center">
          <CalendarIcon className="w-6 h-6 mx-auto mb-2 text-slate-400" />
          Nessun brand disponibile. Creane prima uno nella sezione Brand.
        </div>
      ) : (
        configs.map((cfg, idx) => (
          <BrandRow
            key={cfg.brand_id}
            cfg={cfg}
            onChange={(next) => updateCfg(idx, next)}
            onSave={() => saveCfg(cfg)}
            saving={savingId === cfg.brand_id}
          />
        ))
      )}
    </div>
  );
}
